import * as i0 from '@angular/core';
import { inject, Pipe } from '@angular/core';
import { LocalizationService } from '@odx/angular/localization';
import { getLanguageCode } from '@odx/angular/utils';
import { startWith, distinctUntilChanged, map, shareReplay } from 'rxjs';

/**
 * Service for translating keys into localized text. It listens to changes in the active language
 * and provides translated strings based on the current language preference. The service uses
 * provided `TranslationObject` containing the translations for each supported language.
 *
 * @example
 * ```ts
 * // Using TranslateService in a component
 * class MyComponent {
 *   constructor(private translateService: TranslateService) {
 *     translateService.translate('greeting').subscribe(translatedText => {
 *       console.log(translatedText); // Outputs "Hello" or "Bonjour" based on the active language
 *     });
 *   }
 * }
 * ```
 */
class TranslateService {
    constructor(translations) {
        this.translations = translations;
        this.localizationService = inject(LocalizationService);
        /**
         * An observable that emits the current translation object based on the active language.
         * It ensures that translations are updated dynamically whenever the user's language preference changes.
         *
         * @emits {TranslationObject} activeTranslation$ - The translation object for the active language.
         */
        this.activeTranslation$ = this.localizationService.activeLanguage$.pipe(startWith(this.localizationService.defaultLanguage), distinctUntilChanged(), map((language) => {
            const languageCode = getLanguageCode(language);
            if (this.translations[language]) {
                return this.translations[language];
            }
            if (this.translations[languageCode]) {
                return this.translations[languageCode];
            }
            return this.translations[this.localizationService.defaultLanguage];
        }), shareReplay({ bufferSize: 1, refCount: true }));
    }
    /**
     * Translates a key into its localized text, optionally replacing placeholders with dynamic values.
     * @param {string} key - The translation key to translate.
     * @param {Record<string, unknown>} [data={}] - An object mapping placeholder names to replacement values.
     * @returns {Observable<string>} An observable emitting the translated text, with placeholders replaced by provided values.
     */
    translate(key, data = {}) {
        return this.activeTranslation$.pipe(map((translation) => Object.entries(data).reduce((acc, [variableName, variable]) => {
            const variableRegex = RegExp(`{{\\s*${variableName}\\s*}}`, 'g');
            return acc.replace(variableRegex, variable);
        }, translation[key] ?? '')));
    }
}

class TranslatePipe {
    constructor() {
        this.translateService = inject(TranslateService);
    }
    transform(key, placeholder = {}) {
        return this.translateService.translate(key, placeholder);
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "18.2.13", ngImport: i0, type: TranslatePipe, deps: [], target: i0.ɵɵFactoryTarget.Pipe }); }
    static { this.ɵpipe = i0.ɵɵngDeclarePipe({ minVersion: "14.0.0", version: "18.2.13", ngImport: i0, type: TranslatePipe, isStandalone: true, name: "odxTranslate" }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "18.2.13", ngImport: i0, type: TranslatePipe, decorators: [{
            type: Pipe,
            args: [{
                    standalone: true,
                    name: 'odxTranslate',
                    pure: true,
                }]
        }] });

/**
 * Provides a configuration for Angular's dependency injection system to instantiate the `TranslateService`
 * with a specific set of translations. This function is useful for setting up the translation service
 * at the module level, ensuring that a single instance with a consistent set of translations is used
 * throughout the application.
 *
 * @param {TranslationObject} translations - An object containing key-value pairs where each key is a
 * translation key and the value is the translated string or an object containing translations for
 * nested keys. This object should include translations for all supported languages.
 * @returns {Provider} An Angular provider configuration object that can be used in the `providers` array
 * of an `@NgModule` or `@Component` decorator to supply the `TranslateService` with the provided translations.
 *
 * @example
 * ```ts
 * // Example usage in an Angular module
 * import { NgModule } from '@angular/core';
 * import { provideTranslations } from '@odx/angular/internal/translate';
 *
 * const appTranslations = {
 *   en: { welcome: "Welcome" },
 *   fr: { welcome: "Bienvenue" }
 * };
 *
 * @NgModule({
 *   providers: [
 *     provideTranslations(appTranslations)
 *   ]
 * })
 * export class AppModule { }
 * ```
 */
function provideTranslations(translations) {
    return {
        provide: TranslateService,
        useFactory: () => new TranslateService(translations),
    };
}

/**
 * Generated bundle index. Do not edit.
 */

export { TranslatePipe, TranslateService, provideTranslations };
